import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type TableColumn } from "react-data-table-component";
import { TablePager } from "../shared/TablePager";
import { adminTableTheme } from "./adminTableTheme";
import styles from "./Admin.module.css";
import { Tooltip } from "../shared/Tooltip";

interface MemberLike {
  id: string;
  email: string;
  displayName?: string | null;
}

/**
 * The members of a collection, as a table. The column header names them, and each row has a remove button.
 */
export function MemberTable<TMember extends MemberLike>({
  title,
  emptyMessage,
  removeButtonLabel,
  members,
  formatLabel,
  onRemove,
  readOnly = false,
}: {
  title: string;
  emptyMessage: string;
  removeButtonLabel: string;
  members: TMember[];
  formatLabel: (member: TMember) => string;
  onRemove: (member: TMember) => void | Promise<void>;
  /** Shows the members without a remove control, for members that are derived rather than assigned. */
  readOnly?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const columns = useMemo<TableColumn<TMember>[]>(
    () => [
      { id: "member", name: title, selector: (member) => formatLabel(member), sortable: true, grow: 3 },
      ...(readOnly ? [] : [{
        id: "remove",
        name: <span className="visually-hidden">{t("dataTable.actionsColumn")}</span>,
        button: true,
        ignoreRowClick: true,
        center: true,
        cell: (member: TMember) => (
          <Tooltip label={removeButtonLabel} align="end">
            <button
              type="button"
              className={styles.removeMemberButton}
              aria-label={`${removeButtonLabel}: ${formatLabel(member)}`}
              onClick={() => void onRemove(member)}
            >
              ×
            </button>
          </Tooltip>
        ),
      } satisfies TableColumn<TMember>]),
    ],
    [formatLabel, onRemove, readOnly, removeButtonLabel, t, title],
  );

  // With no members the panel is just its border and a line saying so. With members, the table's own border is the panel's border.
  if (members.length === 0) {
    return (
      <div className={`${styles.roleBox} ${styles.memberPanelInset}`}>
        <p className={styles.note}>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={`${styles.memberTableFrame} ${styles.memberPanelInset} ${styles.peopleTableWrapper}`}>
      <DataTable
        columns={columns}
        data={members}
        keyField="id"
        ariaLabel={title}
        theme={adminTableTheme}
        defaultSortFieldId="member"
        pagination={members.length > 10}
        paginationComponent={TablePager}
        paginationPerPage={10}
        striped
        highlightOnHover
        columnSeparator="subtle"
      />
    </div>
  );
}

/**
 * The search that adds a person to a collection: a box to type a name or email into, and the matches with an add button for each.
 */
export function MemberAddPanel<TMember extends MemberLike>({
  noResultsMessage,
  searchLabel,
  searchPlaceholder,
  addButtonLabel,
  searchId,
  searchValue,
  searchError,
  searchResults,
  onSearchChange,
  onAdd,
  formatLabel,
}: {
  noResultsMessage: string;
  searchLabel: string;
  searchPlaceholder: string;
  addButtonLabel: string;
  searchId: string;
  searchValue: string;
  searchError: string | null;
  searchResults: TMember[];
  onSearchChange: (value: string) => void;
  onAdd: (member: TMember) => void | Promise<void>;
  formatLabel: (member: TMember) => string;
}): React.JSX.Element {
  return (
    <div className={styles.memberSearchPanel}>
      <label htmlFor={searchId}>{searchLabel}</label>
      <input id={searchId} value={searchValue} onChange={(event) => onSearchChange(event.target.value)} placeholder={searchPlaceholder} />

      {searchValue.trim().length > 0 && (
        <div className={styles.memberSearchResults}>
          {searchError ? <p className={styles.feedbackError}>{searchError}</p> : null}
          {searchResults.length > 0 ? (
            <ul className={styles.memberSearchList}>
              {searchResults.map((person) => (
                <li key={person.id} className={styles.memberSearchItem}>
                  <span>{formatLabel(person)}</span>
                  <button type="button" className="button button-secondary" onClick={() => void onAdd(person)}>
                    {addButtonLabel}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {searchResults.length === 0 && searchError == null && <p>{noResultsMessage}</p>}
        </div>
      )}
    </div>
  );
}
